'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'
import { AffLink, ourPlaceLink, OUR_PLACE_PRODUCTS } from '@/app/components/blog/affiliate'

/**
 * Every link here deep-links to the Essentials Trio product page rather than
 * the bare tracking URL, which redirects to the Our Place homepage. Each
 * placement carries its own subId1 so Impact reporting shows which position on
 * the page actually earns.
 */
const buyHref = (subId: string) => ourPlaceLink(OUR_PLACE_PRODUCTS.essentialsTrio, subId)

function Aff({ children, subId }: { children: React.ReactNode; subId: string }) {
  return <AffLink href={buyHref(subId)}>{children}</AffLink>
}

/** Product photography supplied by Our Place. Credited under each image. */
function Shot({ src, alt, caption, subId }: { src: string; alt: string; caption: string; subId: string }) {
  return (
    <figure className="not-prose my-8">
      <a href={buyHref(subId)} rel="sponsored nofollow noopener" target="_blank">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="w-full rounded-xl border border-black/5 bg-white"
        />
      </a>
      <figcaption className="mt-2 text-xs text-gray-500 italic">
        {caption} <span className="not-italic">Image: Our Place.</span>
      </figcaption>
    </figure>
  )
}

function ScoreBadge() {
  return (
    <div className="not-prose my-8 flex items-center gap-5 rounded-xl border border-[#1E6B47]/25 bg-[#1E6B47]/5 p-6">
      <div className="shrink-0 text-center">
        <div className="font-display text-5xl font-bold leading-none text-[#1E6B47]">8.7</div>
        <div className="mt-1 text-xs font-semibold uppercase tracking-wider text-gray-500">out of 10</div>
      </div>
      <div className="text-sm text-gray-700">
        <p className="font-semibold text-ink">Our Place Essentials Pan Trio (8 piece)</p>
        <p className="mt-1">
          Best-in-class on the thing this site measures — there is no
          fluoropolymer in the cooking surface, so there is nothing to shed as
          microplastic. Marked down for a two-to-three-year coating lifespan and
          an undisclosed full formulation. Scoring breakdown below.
        </p>
      </div>
    </div>
  )
}

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Our Place Essentials Pan Trio Review: The Microplastics Verdict"
      author="MicroPlastics Research Desk"
      date="July 31, 2026"
      readTime="16 min read"
      heroImage="/blog/our-place/trio-terracotta.jpg"
      heroImageAlt="Our Place Essentials Pan Trio review terracotta ceramic nonstick 8 piece set microplastics 2026"
    >
      <BlogLastUpdated iso="2026-07-31" formatted="July 31, 2026" />

      {/* FTC 16 CFR Part 255 requires disclosure to be clear, conspicuous and
          proximate to the endorsement. Footer-only placement does not satisfy
          that, so a one-line notice stays here above the review and the full
          text repeats at the foot of the article. Keep both. */}
      <p className="not-prose -mt-2 mb-6 text-xs text-gray-500">
        Gifted sample · affiliate links · we may earn a commission —{' '}
        <a href="#disclosure" className="underline hover:text-gray-700">
          full disclosure
        </a>
      </p>

      <ScoreBadge />

      <BlogTLDR>
        <strong>Recommended, with one limitation nobody in this category will
        tell you plainly.</strong> The Essentials Pan Trio uses Our Place&apos;s{' '}
        <strong>Thermakind sol-gel ceramic coating</strong>, which is
        silica-based rather than fluoropolymer-based. That single fact is why it
        scores as well as it does here: a scratched <strong>PTFE</strong> pan
        sheds actual plastic, roughly <strong>9,100 particles from one
        scratch</strong> and an estimated <strong>2.3 million</strong> from a
        broken coating (Luo et al. 2022). A silica coating has no polymer in it,
        so it has no equivalent shedding mechanism. Independently,{' '}
        <strong>Consumer Reports screened the Always Pan against 96 PFAS
        compounds in 2022 and detected none.</strong> In our own cooking,{' '}
        <strong>eggs released cleanly with no sticking</strong> and meat cooked
        well within the specified heat range. The limitation:{' '}
        <strong>sol-gel ceramic is a consumable.</strong> Budget on replacing it
        every <strong>2&ndash;3 years</strong> — the same as Caraway, GreenPan
        and every other ceramic nonstick. Bought on that understanding, at{' '}
        <strong>$249.95</strong> with a 100-day trial and free returns, it is a
        good buy. Bought expecting heirloom cookware, it is not.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <><strong>Ceramic sol-gel is not plastic.</strong> Thermakind is cured silica — chemically far closer to glass than to Teflon. This is the single strongest reason to prefer it on a microplastics basis, and it is not a marketing distinction, it is a materials one.</>,
          <><strong>It passes the egg test.</strong> Eggs released cleanly with no sticking and no welded ring at the edge of the white, and meat cooked well within the specified heat range. What it will not do is sear at cast-iron temperatures — no ceramic pan will.</>,
          <><strong>The PTFE comparison is not close.</strong> Luo et al. (2022) measured ~9,100 particles from a single scratch and ~2.3 million from a broken coating. Those are lab figures under deliberate abrasion, not a per-meal dose — but the mechanism is specific to polymer coatings and silica does not have it.</>,
          <><strong>Independent verification exists</strong>, which is genuinely rare here: Consumer Reports found none of 96 PFAS compounds in the Always Pan (2022). Caveat: 96 is a panel, not the full universe of thousands of PFAS.</>,
          <><strong>The weak point is lifespan, not safety.</strong> Sol-gel coatings lose release properties in 2&ndash;3 years, mostly through thermal stress rather than scratching. This costs it a full point in our scoring and it is the honest reason not to buy it as your only cookware.</>,
          <><strong>Pressed aluminium body.</strong> Never a food-contact surface while the coating is intact, and the reason the pans are light and heat fast. It is also why a chipped pan should be retired, not nursed.</>,
          <><strong>Our Place does not publish the full coating formulation</strong> or its own third-party test reports. &ldquo;Made without PFAS, PTFE, PFOA, lead, cadmium&rdquo; states what is absent, not what is present.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How we scored this, and what our score is based on
      </h2>
      <p>
        A number is worthless if you cannot see how it was reached, so here is
        the whole rubric. We weight heavily toward the things this site actually
        has expertise in — coating chemistry, particle shedding, independent
        testing — and comparatively lightly toward things every other cookware
        review already covers well.
      </p>
      <p>
        <strong>Basis of assessment.</strong> Materials, chemistry, published
        specifications and independent laboratory testing are assessed directly
        from primary sources, all cited at the foot of this article.{' '}
        <em>Cooking performance</em> is scored from our own hands-on cooking on
        the set, described in full below, cross-checked against Our Place&apos;s
        published specifications and the aggregate owner rating on the product
        page (<strong>4.6 out of 5 across 68 reviews</strong>, checked 30 July
        2026).
      </p>
      <p>
        What we do <strong>not</strong> yet have is long-term wear data. Every
        ceramic pan performs well in its first weeks — the entire question in
        this category is what it looks like at month eighteen. Our durability
        score below reflects the known behaviour of sol-gel coatings generally,
        not a claim about this specific set over time, and{' '}
        <strong>we will update this review as the coating ages</strong> rather
        than leave a first-impressions verdict standing as if it were a
        long-term one.
      </p>

      <BlogComparisonTable
        caption="Scoring rubric — Our Place Essentials Pan Trio (8 piece)"
        columns={[
          { key: 'criterion', label: 'Criterion' },
          { key: 'weight', label: 'Weight' },
          { key: 'score', label: 'Score' },
          { key: 'why', label: 'Reasoning' },
        ]}
        rows={[
          { criterion: 'Microplastic shedding risk', weight: '30%', score: <><strong>10</strong> / 10</>, why: <>No fluoropolymer in the cooking surface. There is no polymer present to release as micro- or nanoplastic particles. This is the maximum achievable for a nonstick pan.</> },
          { criterion: 'Durability &amp; lifespan', weight: '20%', score: <><strong>7</strong> / 10</>, why: <>2&ndash;3 years of realistic service life. Average for sol-gel ceramic, well behind PTFE (3&ndash;5 years) and nowhere near cast iron or stainless. The category&apos;s weak point, not a defect unique to Our Place.</> },
          { criterion: 'Cooking performance', weight: '15%', score: <><strong>9</strong> / 10</>, why: <>Eggs released cleanly with no sticking and no edge weld; meat cooked well within the specified heat range; light, fast and even from the pressed-aluminium base. Held back one point only because no ceramic pan can sear at cast-iron temperatures.</> },
          { criterion: 'Materials &amp; build', weight: '10%', score: <><strong>9</strong> / 10</>, why: <>Cast stainless handles, tempered glass lids with silicone rims, stainless knobs. No plastic anywhere in the heat path — a meaningful detail that many competitors get wrong with phenolic handles.</> },
          { criterion: 'Independent verification', weight: '10%', score: <><strong>8</strong> / 10</>, why: <>The 2022 Consumer Reports 96-compound PFAS screen is real third-party testing and rare in this category. Docked because 96 compounds is a sample of thousands, and the test covered the Always Pan rather than this specific coating generation.</> },
          { criterion: 'Value for money', weight: '10%', score: <><strong>9</strong> / 10</>, why: <>$249.95 for three pans, three lids and two storage racks against a $332 individual-purchase value, plus free shipping, a 100-day trial and free returns. Strong on purchase price. (Cost per year is handled under lifespan, so it is not double-counted here.)</> },
          { criterion: 'Transparency', weight: '5%', score: <><strong>7</strong> / 10</>, why: <>Clear, specific exclusion claims and full published care instructions and dimensions. Docked because the complete coating formulation is not disclosed and Our Place has not published third-party test reports of its own.</> },
          { criterion: <><strong>Weighted total</strong></>, weight: <><strong>100%</strong></>, score: <><strong>8.7</strong> / 10</>, why: <>Excellent on chemistry and materials; held back by a consumable coating and incomplete disclosure.</> },
        ]}
      />

      <Shot
        subId="review-hero-set"
        src="/blog/our-place/set-terracotta.jpg"
        alt="Our Place Essentials Pan Trio in Terracotta, three ceramic nonstick fry pans with glass lids and storage racks"
        caption="The 8-piece Essentials Pan Trio in Terracotta: 8″, 10″ and 12″ pans, three lids, two storage racks."
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What is in the box
      </h2>
      <p>
        The <Aff subId="review-cta-1">Essentials Pan Trio</Aff> is an 8-piece set: ceramic nonstick
        fry pans in <strong>8&Prime;, 10&Prime; and 12&Prime;</strong>, a
        tempered-glass lid for each, and two storage racks. List price{' '}
        <strong>$249.95</strong> against a stated $332 value bought
        individually. Four colourways — Cream, Terracotta, Char, Blue Salt. Our
        Place sent us Terracotta; we chose the colour, not the contents.
      </p>

      <BlogComparisonTable
        caption="Materials and operating limits, from Our Place's published specifications (checked 30 July 2026)"
        columns={[
          { key: 'part', label: 'Component' },
          { key: 'material', label: 'Material' },
          { key: 'note', label: 'What it means for exposure' },
        ]}
        rows={[
          { part: 'Cooking surface', material: <>Thermakind&reg; sol-gel ceramic nonstick</>, note: <>Silica-based, not a fluoropolymer. No PTFE means no fluoropolymer particles to shed. This is the reason the set scores 10/10 on our heaviest-weighted criterion.</> },
          { part: 'Pan body', material: 'Pressed aluminium', note: <>Conductive and light. Never contacts food <em>unless</em> the coating chips — which is the point at which the pan should be replaced, not repaired.</> },
          { part: 'Lids', material: 'Tempered glass, silicone rim, brushed stainless knob', note: 'Glass over the food rather than plastic. Silicone is not a meaningful microplastic source in normal use.' },
          { part: 'Handles', material: 'Solid cast stainless steel, silicone node', note: 'No phenolic or filled-plastic handles anywhere in the heat path.' },
          { part: 'Stated exclusions', material: <>Made without PFAS, PTFE, PFOA, lead, cadmium</>, note: <>Our Place&apos;s own claim, independently supported for PFAS by the 2022 Consumer Reports screen — which covered 96 compounds, not all of them.</> },
          { part: 'Heat limits', material: <>Oven safe to 450&deg;F; glass lids to 425&deg;F</>, note: 'Lower than cast iron or stainless. Not a pan for broiling or ripping-hot searing.' },
          { part: 'Care', material: 'Hand wash only; no metal utensils; low to medium heat', note: 'Adherence here is the single biggest variable in how long the coating lasts.' },
          { part: 'Purchase terms', material: <>Free shipping, 100-day trial, free returns</>, note: 'Genuinely lowers the risk of trying it — you can return it after months of use.' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How it actually cooks
      </h2>
      <p>
        Chemistry is why this set is on the site. It is not why you would
        actually enjoy owning it, so: we have been cooking on it.
      </p>
      <p>
        <strong>Eggs are the honest test of any nonstick pan, and it passed
        cleanly.</strong> Fried and scrambled, the eggs released without
        sticking and without needing to be chased around the pan with a spatula.
        Nothing gripped the surface, nothing tore, and there was no welded-on
        ring left behind at the edge of the white — which is the usual failure
        point on a mediocre ceramic pan and the first thing to go as a coating
        ages. Clean-up was a wipe rather than a scrub.
      </p>
      <p>
        <strong>Meat was the more interesting test</strong>, because it is where
        ceramic pans usually reveal their limits. Ceramic wants low-to-medium
        heat, and searing wants high heat, so the two goals are in tension by
        design. Cooked within the range Our Place specifies, it handled meat
        well, and the pressed-aluminium base is genuinely quick and even to heat
        — you are not fighting cold spots.
      </p>
      <p>
        The caveat is structural rather than a fault: <strong>if you want a hard
        crust on a steak, this is not the pan, and no ceramic pan is.</strong>{' '}
        That is what cast iron and carbon steel are for. Push a sol-gel coating
        to searing temperatures and you will get your crust once, then spend the
        next year watching the release properties disappear. Used as intended,
        for everyday cooking rather than restaurant-style searing, it performs.
      </p>
      <p>
        On handling, the specifications translate into real-world behaviour:
        the pans are noticeably light for their size, the cast stainless handles
        stay cool enough to grab on the stovetop, and the three-size spread
        covers most of what a home kitchen actually does day to day. The storage
        racks looked like a marketing flourish and are not — stacking bare pans
        is the most common way ceramic coatings get scratched, and
        keeping them separated is a genuine contribution to lifespan rather than
        a countertop aesthetic.
      </p>
      <p className="text-sm text-gray-500 italic">
        First impressions after initial use. We will add long-term findings as
        the coating ages, including whether release holds up past the
        twelve-month mark, which is the point at which ceramic pans typically
        start to decline.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The part that actually matters: sol-gel ceramic vs PTFE
      </h2>
      <p>
        Most cookware coverage treats &ldquo;nonstick&rdquo; as a single
        category with a safety asterisk. For microplastics specifically, the two
        dominant coatings are not comparable, and the gap is wider than most
        buyers realise.
      </p>
      <p>
        <strong>PTFE, sold as Teflon, is a plastic.</strong>{' '}
        Polytetrafluoroethylene is a fluoropolymer. When the surface is damaged,
        it releases polymer fragments into food. Luo and colleagues quantified
        this in 2022 using Raman imaging and a purpose-built counting algorithm:
        a <strong>single scratch released roughly 9,100 particles</strong>, and
        a broken, degraded coating released an estimated{' '}
        <strong>2.3 million micro- and nanoplastic particles</strong>.
      </p>
      <p>
        Two honest caveats on that study, because we would rather you heard them
        from us. Those are <em>laboratory</em> figures produced under deliberate
        abrasion, not a measurement of what lands on a dinner plate. And PTFE is
        biologically inert and largely passes through the gut — the concern is
        cumulative and environmental, not acute poisoning. Anyone quoting
        &ldquo;2.3 million particles in your food&rdquo; is misusing the paper.
      </p>
      <p>
        <strong>Sol-gel ceramic is not a polymer at all.</strong> Thermakind is
        a silica-based coating applied as a liquid sol and cured to a hard,
        glass-like gel — Our Place describes it as built primarily from silica,
        water and alcohol. When a silica coating wears, it does not shed
        microplastics, because there is no plastic in it to shed. It loses
        slickness instead.
      </p>
      <p>
        That is the whole case for this set, and it is a strong one. If your
        reason for replacing cookware is microplastics, moving off scratched
        Teflon onto any competent ceramic pan is among the highest-value swaps
        available in a kitchen. This is a competent ceramic pan.
      </p>

      <Shot
        subId="review-inline-pan"
        src="/blog/our-place/pan-terracotta.jpg"
        alt="Close-up of Our Place Essentials ceramic nonstick fry pan in Terracotta with cast stainless steel handle"
        caption="Cast stainless handle with a silicone node — no plastic in the heat path, which is more than several competitors manage."
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Where we mark it down
      </h2>
      <p>
        Three deductions. All three apply to the ceramic category generally
        rather than to Our Place specifically, and none of them changed because
        the set arrived free.
      </p>
      <p>
        <strong>1. It is a consumable (&minus;0.60 weighted, the largest single
        deduction).</strong> Sol-gel
        ceramic loses release properties faster than PTFE, typically over{' '}
        <strong>two to three years</strong> of regular use. Crucially, the main
        mechanism is <em>thermal</em>, not mechanical: repeated high heat
        gradually breaks down the cured silica network. This has been our
        standing position on every ceramic pan we cover, including Caraway and
        GreenPan, and it stays our position now that we earn a commission on
        this one. At $249.95 on a three-year cycle you are spending roughly $83
        a year on three pans. Cast iron costs $25 once.
      </p>
      <p>
        <strong>2. The formulation is not fully disclosed (&minus;0.15
        weighted).</strong>{' '}
        Our Place states what the coating is made <em>without</em>. It does not
        publish the complete composition, and it has not released third-party
        test reports of its own despite indicating some years ago that it would.
        Sol-gel coatings in this class commonly include metal-oxide additives
        for colour, hardness and adhesion. Nothing about that is alarming;
        nothing about it is verified either. Our rule holds: when a brand does
        not disclose, we say so rather than guessing.
      </p>
      <p>
        <strong>3. Ceramic coatings are not perfectly inert (no separate
        deduction — it is a usage caveat, not a defect).</strong>{' '}
        Ntim et al. (2017) found measurable migration of ionic titanium, silicon
        and aluminium from ceramic-coated cookware under acidic conditions. That
        is <em>ionic</em> migration, not particle shedding, and it is not a
        microplastics finding — but it is a good reason not to simmer tomato
        sauce for three hours in any ceramic-coated aluminium pan. Use stainless
        or enamel for that.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How it compares
      </h2>

      <BlogComparisonTable
        caption="Ceramic nonstick and the alternatives, on the criteria this site weights"
        columns={[
          { key: 'set', label: 'Cookware' },
          { key: 'coating', label: 'Coating' },
          { key: 'shed', label: 'Sheds microplastics?' },
          { key: 'life', label: 'Realistic life' },
          { key: 'price', label: 'Price' },
        ]}
        rows={[
          { set: <><strong>Our Place Essentials Pan Trio</strong></>, coating: <>Thermakind&reg; sol-gel ceramic on aluminium</>, shed: <><strong>No</strong> — silica, not polymer</>, life: '2–3 years', price: '$249.95 (3 pans + lids + storage)' },
          { set: 'Caraway Fry Pan', coating: 'Sol-gel ceramic on aluminium', shed: <><strong>No</strong></>, life: '2–3 years', price: '~$95 (single pan)' },
          { set: 'GreenPan Valencia Pro', coating: <>Thermolon&trade; sol-gel ceramic</>, shed: <><strong>No</strong></>, life: '2–3 years', price: '$75–$120' },
          { set: 'Any PTFE / Teflon nonstick', coating: 'Fluoropolymer', shed: <><strong>Yes</strong> — ~9,100 particles per scratch (Luo 2022)</>, life: '3–5 years', price: 'Varies' },
          { set: 'Lodge cast iron', coating: 'None — seasoned iron', shed: <><strong>No</strong></>, life: 'Lifetime', price: '$20–$40' },
          { set: 'All-Clad D3 stainless', coating: 'None', shed: <><strong>No</strong></>, life: 'Lifetime', price: '$100+' },
          { set: 'Xtrema pure ceramic', coating: <>None — solid ceramic body</>, shed: <><strong>No</strong></>, life: 'Lifetime if not dropped', price: '$80–$200' },
        ]}
      />
      <p>
        Read that honestly and it says two things. Against other ceramic sets,
        Our Place competes on design, storage and the fact that its coating has
        actually been independently screened — not on being chemically
        different. Against cast iron and stainless it loses decisively on
        longevity and cost per year, and wins on being genuinely nonstick for
        eggs, fish and pancakes.
      </p>
      <p>
        Which is the real recommendation, and it is the one the category rarely
        makes: <strong>ceramic nonstick should be one or two pans in a kitchen,
        not the whole kitchen.</strong> Cast iron and stainless for searing,
        braising and anything acidic or long-cooked. Ceramic for the handful of
        things that genuinely need release.
      </p>

      <Shot
        subId="review-inline-storage"
        src="/blog/our-place/storage-system.jpg"
        alt="Our Place Essentials storage rack holding ceramic nonstick fry pans upright"
        caption="The storage racks are the real design differentiator — stacking is what scratches ceramic coatings fastest."
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Getting three years instead of eighteen months
      </h2>
      <p>
        Coating lifespan is the single biggest factor in whether this set is
        good value, and it is almost entirely under your control:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Low to medium heat, always.</strong> Thermal stress kills sol-gel ceramic faster than scratching does. This is the number one rule and the most commonly broken one.</li>
        <li><strong>Never preheat empty.</strong> An empty pan reaches damaging temperatures within a minute or two.</li>
        <li><strong>Hand wash only.</strong> Our Place is explicit. Dishwasher detergent is abrasive and strongly alkaline and will visibly dull the surface.</li>
        <li><strong>Wood, silicone or nylon utensils.</strong> No metal.</li>
        <li><strong>Let it cool before washing.</strong> Thermal shock crazes the coating.</li>
        <li><strong>Use the racks.</strong> Stacking bare pans is how coatings get scratched in storage — which is precisely why the storage system is more than a gimmick.</li>
        <li><strong>Skip the cooking spray.</strong> Aerosol propellants and emulsifiers polymerise into a sticky residue that is very hard to remove and destroys release.</li>
        <li><strong>Retire it when it chips.</strong> Exposed aluminium is a food-contact surface. That is a replacement trigger, not a cook-around-it problem.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Who should buy it
      </h2>
      <p>
        <strong>Buy it if</strong> you are replacing scratched Teflon; you want
        nonstick without fluoropolymers; you cook eggs, fish or pancakes
        regularly; you are short on cabinet space; and you accept a
        two-to-three-year replacement cycle. The 100-day trial and free returns
        mean you can test that judgement for months at essentially no risk,
        which is unusually generous and worth using.
      </p>
      <p>
        <strong>Don&apos;t buy it if</strong> you want cookware for life; you
        cook mostly acidic or long-simmered dishes; you sear at high heat; you
        want everything dishwasher-safe; or you are optimising cost per year.
        Cast iron and stainless beat it on every one of those, and cost less.
      </p>
      <p>
        <Aff subId="review-cta-2">Check current pricing and colours on the Essentials Pan Trio &rarr;</Aff>
      </p>

      <BlogAppCTA
        headline="Cookware is one exposure route. Check the rest."
        sub="Storage containers, utensils, kettles and food packaging are usually bigger microplastic sources than your pan. Scan what you already own and get a ranked list of what to change first."
        buttonLabel="Scan your kitchen"
      />

      <p>
        See also our{' '}
        <Link href="/blog/best-non-toxic-cookware" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          best non-toxic cookware guide
        </Link>
        ,{' '}
        <Link href="/blog/microplastics-in-cooking-utensils" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in cooking utensils
        </Link>
        ,{' '}
        <Link href="/blog/black-plastic-kitchen-utensils" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          black plastic kitchen utensils
        </Link>
        , and{' '}
        <Link href="/blog/best-plastic-free-food-storage" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastic-free kitchen swaps
        </Link>
        .
      </p>

      <BlogWhatTheAppChecks
        items={[
          <>Cookware coating type from the barcode — PTFE, sol-gel ceramic, enamel, bare iron or stainless.</>,
          <>Whether the coating is a fluoropolymer, the distinction that decides if a worn pan sheds plastic at all.</>,
          <>Utensil and storage materials, usually a larger kitchen exposure route than the pan itself.</>,
          <>Heat limits, so you know which pans are oven-safe and which degrade on high heat.</>,
          <>A ranked replace-first list based on what you actually own.</>,
        ]}
      />

      <BlogFAQ
        items={[
          {
            question: 'Does Our Place cookware release microplastics?',
            answer:
              'Not the way a Teflon pan does. Our Place uses Thermakind, a sol-gel ceramic coating that is silica-based rather than a fluoropolymer, so there is no polymer in the cooking surface to shed as microplastic particles. By contrast PTFE is a plastic: Luo et al. (2022) measured roughly 9,100 particles released from a single scratch and about 2.3 million from a broken coating. If microplastics are your reason for changing cookware, ceramic is the meaningful upgrade.',
          },
          {
            question: 'What score did the Our Place Essentials Pan Trio get?',
            answer:
              '8.7 out of 10. It scores a maximum 10/10 on microplastic shedding risk, our most heavily weighted criterion at 30%, because there is no fluoropolymer in the cooking surface. It scores 9/10 on cooking performance, materials and build, and value; 8/10 on independent verification; and 7/10 on both durability and transparency. The two things holding it back are a two-to-three-year coating lifespan and the fact that Our Place does not publish the complete coating formulation.',
          },
          {
            question: 'Is the Our Place Essentials Pan Trio worth $249.95?',
            answer:
              'It depends on the comparison. Against other ceramic nonstick sets it is competitively priced for three pans, three lids and storage, and it is one of very few whose coating has been independently screened for PFAS. Against cast iron or stainless steel it is expensive per year of use, because ceramic nonstick realistically lasts two to three years while cast iron lasts a lifetime for a fraction of the price. Our recommendation is to own one or two ceramic pans for eggs and fish and use cast iron or stainless for everything else. The 100-day trial and free returns make testing that judgement low-risk.',
          },
          {
            question: 'Is Thermakind coating safe?',
            answer:
              'The available evidence is reassuring but incomplete. Consumer Reports tested the Our Place Always Pan against 96 PFAS compounds in 2022 and detected none, which is genuine independent verification and rare in this category. The limits worth knowing: 96 compounds is a panel rather than the full universe of PFAS, Our Place does not publish the complete coating formulation, and it has not released third-party test reports of its own. Separately, Ntim et al. (2017) found ionic titanium, silicon and aluminium migration from ceramic cookware under acidic conditions, which is a reason to avoid long acidic cooking in any ceramic-coated pan.',
          },
          {
            question: 'How long does Our Place ceramic nonstick last?',
            answer:
              'Realistically two to three years of regular use, the same as Caraway, GreenPan and every other sol-gel ceramic pan. The main degradation mechanism is thermal rather than mechanical, so the highest-impact thing you can do is cook on low to medium heat and never preheat the pan empty. Hand washing, avoiding metal utensils, skipping aerosol cooking spray and letting the pan cool before washing all extend life. Treat ceramic nonstick as a consumable, not heirloom cookware.',
          },
          {
            question: 'Is the aluminium body a problem?',
            answer:
              'Not while the coating is intact, because the aluminium never contacts food. Pressed aluminium is used because it is highly conductive and light, which is why these pans heat quickly and stay easy to handle. It does mean a chipped or worn-through pan should be retired rather than kept in service, since at that point aluminium becomes a food-contact surface. This is true of every ceramic-coated aluminium pan, not just Our Place.',
          },
          {
            question: 'Can you put Our Place Essentials in the dishwasher?',
            answer:
              'No. Our Place specifies hand wash only for this range and that instruction is worth following. Dishwasher detergents are abrasive and strongly alkaline, and they degrade sol-gel ceramic noticeably faster than hand washing. Since coating lifespan is the main determinant of whether the set is good value, dishwashing it is the fastest way to turn a good purchase into a bad one.',
          },
          {
            question: 'Ceramic nonstick or cast iron?',
            answer:
              'Both, for different jobs. Cast iron and stainless steel shed nothing, last a lifetime and cost far less per year, so they should form the foundation of a low-plastic kitchen and are what we recommend for searing, braising and acidic or long-cooked dishes. Ceramic nonstick earns its place for the specific things that genuinely need release, such as eggs, fish and pancakes, where cast iron demands more skill and seasoning. Buying ceramic for the entire kitchen is the expensive mistake.',
          },
          {
            question: 'What is the return policy?',
            answer:
              'Our Place offers free shipping, a 100-day trial and free returns on this set. That is unusually generous for cookware and it materially lowers the risk of trying it, because 100 days is long enough to see how the coating holds up to your actual cooking habits rather than just how it performs on day one.',
          },
        ]}
      />

      <div
        id="disclosure"
        className="not-prose mt-10 scroll-mt-24 border-t border-black/10 pt-4 text-xs leading-relaxed text-gray-500"
      >
        <strong className="font-semibold text-gray-600">Disclosure:</strong> Our
        Place sent us this cookware set free of charge, and we earn a commission
        if you buy through the links on this page. We were not paid for this
        review, Our Place did not see it before publication, and it had no
        approval over what we wrote. Product photography is Our Place&apos;s.
        Our{' '}
        <Link href="/affiliate-disclosure" className="underline hover:text-gray-700">
          full disclosure and review policy is here
        </Link>
        .
      </div>

      <BlogSources
        items={[
          {
            authors: 'Luo Y, Gibson CT, Chuah C, Tang Y, Naidu R, Fang C',
            year: 2022,
            title: 'Raman imaging for the identification of Teflon microplastics and nanoplastics released from non-stick cookware',
            publisher: 'Science of The Total Environment (DOI: 10.1016/j.scitotenv.2022.158293)',
            url: 'https://www.sciencedirect.com/science/article/abs/pii/S004896972205392X',
          },
          {
            authors: 'Consumer Reports',
            year: 2022,
            title: 'You can’t always trust claims on non-toxic cookware — screened the Our Place Always Pan and other pans against 96 PFAS compounds',
            publisher: 'Consumer Reports',
            url: 'https://www.consumerreports.org/toxic-chemicals-substances/you-cant-always-trust-claims-on-non-toxic-cookware-a4849321487/',
          },
          {
            authors: 'Ntim SA, Thomas TA, Begley TH, Noonan GO',
            year: 2017,
            title: 'Characterisation and potential migration of metals from ceramic-coated cookware under acidic conditions',
            publisher: 'Food Control / Food Additives & Contaminants',
            url: 'https://www.sciencedirect.com/journal/food-control',
          },
          {
            authors: 'Our Place',
            year: 2026,
            title: 'Essentials Pan Trio (8 piece): published specifications, materials, care instructions, pricing and aggregate customer rating (4.6/5, 68 reviews), retrieved 30 July 2026',
            publisher: 'fromourplace.com',
            url: 'https://fromourplace.com/products/essentials-pan-trio-8pc',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
